import { useEffect, useState } from "react";
import { X } from "lucide-react";
import api from "../api/axios";
import DataTable from "./DataTable";

const formatMoney = (value) => new Intl.NumberFormat("en-IN", {
  style: "currency",
  currency: "INR",
  maximumFractionDigits: 0,
}).format(Number(value) || 0);

const formatDate = (value) => value ? new Date(value).toLocaleString("en-IN", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" }) : "-";
const titleCase = (value = "") => value.charAt(0).toUpperCase() + value.slice(1);

export default function ShopOrderHistoryDialog({ shop, range, endpoint, onClose }) {
  const [history, setHistory] = useState(null);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    let active = true;
    setLoading(true);
    setError("");
    api.get(endpoint, { params: { range, page, limit: 8 } })
      .then(({ data }) => { if (active) setHistory(data); })
      .catch((requestError) => {
        if (active) setError(requestError.response?.data?.message || "Could not load this shop's order history.");
      })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [endpoint, range, page]);

  useEffect(() => {
    const closeOnEscape = (event) => { if (event.key === "Escape") onClose(); };
    window.addEventListener("keydown", closeOnEscape);
    return () => window.removeEventListener("keydown", closeOnEscape);
  }, [onClose]);

  return <div className="district-history-overlay" onMouseDown={(event) => { if (event.target === event.currentTarget) onClose(); }}>
    <section className="district-history-dialog" role="dialog" aria-modal="true" aria-labelledby="shop-history-title">
      <header>
        <div>
          <span>SHOP ORDER FLOW</span>
          <h2 id="shop-history-title">{history?.shop?.name || shop.name}</h2>
          <p>{history?.shop?.owner || shop.owner} · {history?.shop?.distributor || shop.distributor} · {history?.shop?.salesperson || shop.salesperson}</p>
        </div>
        <button type="button" title="Close order history" aria-label="Close order history" onClick={onClose}><X size={18} /></button>
      </header>
      <div className="district-history-body">
        <div className="district-history-caption">{history?.range?.label || titleCase(range)} · {history?.pagination?.total || 0} orders</div>
        {error && <div className="dashboard-error" role="alert">{error}</div>}
        {loading && <div className="dashboard-loading" role="status">Loading shop orders…</div>}
        {history?.orders?.length ? <DataTable headers={["Order", "Placed", "Items", "Base", "Discount", "Taxable", "GST", "Total", "Status"]} wrapStyle={{ border: 0, borderRadius: 0 }} tableStyle={{ minWidth: 0 }}>
          {history.orders.map((order) => <tr key={order.id}>
            <td><strong>#{order.number}</strong>{order.notes && <small>{order.notes}</small>}</td>
            <td>{formatDate(order.createdAt)}</td>
            <td><div className="district-history-items">{order.items.map((item, index) => <span key={`${item.name}-${item.size}-${index}`}>{item.name} · {item.size} × {item.quantity}</span>)}</div></td>
            <td>{order.baseAmount == null ? "-" : formatMoney(order.baseAmount)}</td>
            <td>{order.discountPercent == null ? "-" : `${order.discountPercent}% · -${formatMoney(order.discountAmount)}`}</td>
            <td>{order.taxableAmount == null ? "-" : formatMoney(order.taxableAmount)}</td>
            <td>{order.gstAmount == null ? "Not recorded" : `${order.gstPercent}% · ${formatMoney(order.gstAmount)}`}</td>
            <td>{formatMoney(order.totalAmount)}</td>
            <td><span className={`dashboard-order-status status-${order.status}`}>{titleCase(order.status)}</span></td>
          </tr>)}
        </DataTable> : !loading && !error && <div className="district-empty">No shop orders in this date range.</div>}
        {history?.pagination?.pages > 1 && <div className="district-pagination"><span>Page {history.pagination.page} of {history.pagination.pages}</span><button type="button" disabled={page <= 1 || loading} onClick={() => setPage((current) => current - 1)}>Previous</button><button type="button" disabled={page >= history.pagination.pages || loading} onClick={() => setPage((current) => current + 1)}>Next</button></div>}
      </div>
    </section>
  </div>;
}
